import { LANGUAGE, VITE_APPLICATION_NAME } from '@portal/config/env';
import { getTransactionName } from '@portal/enum/transactions';
import { TransactionType } from '@portal/models/enumerators';
import { ApproveTransaction } from '@portal/models/module';
import { translate } from '@portal/services/i18n';
import { isCpf, maskHiddenCpfOrCnpj } from '@portal/services/masks';
import { moneyFormat } from '@portal/utils/format';
import { validationStatus } from '@portal/utils/transaction';
import { Card, Modal } from 'antd';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';

interface ManualReceivableModalProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  data: ApproveTransaction | undefined;
}

const ApprovedTransactionDetailModal = ({
  visible,
  setVisible,
  data,
}: ManualReceivableModalProps): JSX.Element => {
  const isPix = data?.transactionRequest?.type === TransactionType.PIX;

  const favoredTaxForPix = useMemo(() => {
    if (isPix && data?.transactionRequest?.favored?.taxIdentifier) {
      return maskHiddenCpfOrCnpj(data.transactionRequest.favored.taxIdentifier);
    }
    return null;
  }, [isPix, data?.transactionRequest?.favored?.taxIdentifier]);

  const favoredNameForPix = isPix && data?.transactionRequest?.favored?.alias;
  const favoredBankAgencyForPix =
    isPix && data?.transactionRequest?.favored?.bankAgency;

  const bankName = useMemo(() => {
    if (data?.transactionRequest?.type === TransactionType.P2P) {
      return VITE_APPLICATION_NAME;
    }

    if (isPix && data?.transactionRequest?.favored?.bankCode) {
      return data?.transactionRequest?.favored?.bankCode;
    }

    if (data?.transactionRequest?.favored?.bankUserName) {
      return data?.transactionRequest?.favored?.bankUserName;
    }

    return '_';
  }, [
    data?.transactionRequest?.type,
    data?.transactionRequest?.favored?.bankCode,
    isPix,
    data?.transactionRequest?.favored?.bankUserName,
  ]);

  const renderTransactionDetails = () => {
    const DetailItem = ({
      label,
      value,
      className = 'approved-transaction-detail-modal__container__answer-value',
    }: {
      label: string;
      value: React.ReactNode;
      className?: string;
    }) => (
      <div>
        <p className="approved-transaction-detail-modal__container__label-value">
          {label}
        </p>
        <p className={className}>{value ?? '_'}</p>
      </div>
    );

    const items = [
      {
        label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.LABEL_VALUE'),
        value: moneyFormat(data?.transactionRequest?.amount) ?? '_',
      },
      {
        label: '',
        value: data?.status ? validationStatus(data.status) : '_',
        className:
          'approved-transaction-detail-modal__container__status-transaction',
      },
      'separator',
      {
        label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.REQUESTER'),
        value: data?.transactionRequest?.account?.name ?? '_',
        className:
          'approved-transaction-detail-modal__container__requester-value',
      },
      {
        label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.REQUEST_IN'),
        value: data?.createdAt
          ? DateTime.fromISO(data.createdAt).toFormat(
              translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
            )
          : '_',
        className:
          'approved-transaction-detail-modal__container__requester-value',
      },
      {
        label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.PAYMENT_DATE'),
        value: data?.transactionRequest?.transactionScheduleAt
          ? DateTime.fromISO(
              data.transactionRequest.transactionScheduleAt
            ).toFormat('dd/MM/yyyy', { locale: LANGUAGE })
          : translate('PAGES.HOME.APPROVED.TABLE.AFTER_APPROVAL'),
        className:
          'approved-transaction-detail-modal__container__requester-value',
      },
      'separator',
      {
        label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.WHO_REQUEST'),
        value:
          data?.transactionRequest?.favored?.alias ??
          data?.transactionRequest?.favored?.bankUserName ??
          favoredNameForPix ??
          '_',
      },
      {
        label: translate(
          `COMPONENTS.CONFIRMATION_DETAILS.TAX_IDENTIFIER.${
            isCpf(data?.transactionRequest?.favored?.taxIdentifier ?? '')
              ? 'PERSON'
              : 'COMPANY'
          }`
        ),
        value: data?.transactionRequest?.favored?.taxIdentifier
          ? maskHiddenCpfOrCnpj(data.transactionRequest.favored.taxIdentifier)
          : favoredTaxForPix || '-',
      },
      'separator',
      {
        label: translate(
          'PAGES.HOME.APPROVED.DETAILS.CONTAINER.TYPE_TRANSACTION'
        ),
        value: data?.transactionRequest?.type
          ? getTransactionName(data.transactionRequest.type)
          : '_',
        className:
          'approved-transaction-detail-modal__container__destiny-value',
      },
      {
        label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.INSTITUTION'),
        value: bankName,
        className:
          'approved-transaction-detail-modal__container__destiny-value',
      },
      ...(data?.transactionRequest?.favored?.bankAgency ||
      favoredBankAgencyForPix
        ? [
            {
              label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.AGENCY'),
              value:
                data?.transactionRequest?.favored?.bankAgency ??
                favoredBankAgencyForPix,
              className:
                'approved-transaction-detail-modal__container__destiny-value',
            },
          ]
        : []),
      ...(data?.transactionRequest?.favored?.bankAccount
        ? [
            {
              label: translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.ACCOUNT'),
              value: `${data.transactionRequest.favored.bankAccount}${
                data.transactionRequest.favored.bankAccountDigit
                  ? `-${data.transactionRequest.favored.bankAccountDigit}`
                  : ''
              }`,
              className:
                'approved-transaction-detail-modal__container__destiny-value',
            },
          ]
        : []),
    ];

    return (
      <Card>
        <div className="approved-transaction-detail-modal__row">
          {items.map((item) =>
            item === 'separator' ? (
              <div
                key={item}
                className="approved-transaction-detail-modal__container__separator"
              />
            ) : typeof item === 'object' && item !== null ? (
              <DetailItem key={item.label} {...item} />
            ) : null
          )}
        </div>
      </Card>
    );
  };

  return (
    <Modal
      open={visible}
      footer={null}
      onCancel={() => setVisible(false)}
      width={700}
      centered
      title={translate('PAGES.HOME.APPROVED.DETAILS.CONTAINER.TITLE')}
    >
      {renderTransactionDetails()}
    </Modal>
  );
};

export default ApprovedTransactionDetailModal;
